import React, { useState, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  alpha,
  useTheme
} from '@mui/material';
import {
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  RestartAlt as ResetIcon,
  Image as ImageIcon
} from '@mui/icons-material';

interface ImageViewerProps {
  imageUrl?: string;
  altText?: string;
  title?: string;
}

const ImageViewer: React.FC<ImageViewerProps> = ({
  imageUrl,
  altText = "Lesion for ABCDE analysis",
  title = "Lesion Image"
}) => {
  const theme = useTheme();
  const [zoomLevel, setZoomLevel] = useState<number>(1);
  const [imagePosition, setImagePosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  const handleZoomIn = () => setZoomLevel(prev => Math.min(prev + 0.5, 5));
  const handleZoomOut = () => setZoomLevel(prev => Math.max(prev - 0.5, 0.5));
  const handleResetZoom = () => {
    setZoomLevel(1);
    setImagePosition({ x: 0, y: 0 });
  };

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    setIsDragging(true);
    setDragStart({ x: e.clientX - imagePosition.x, y: e.clientY - imagePosition.y });
  }, [imagePosition]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (isDragging) {
      setImagePosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  }, [isDragging, dragStart]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  if (!imageUrl) {
    return null;
  }

  return (
    <Card sx={{ height: 'fit-content' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ImageIcon color="primary" />
            {title}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton onClick={handleZoomOut} size="small" disabled={zoomLevel <= 0.5}>
              <ZoomOutIcon />
            </IconButton>
            <Typography variant="body2" sx={{ px: 1, py: 0.5, minWidth: 60, textAlign: 'center' }}>
              {Math.round(zoomLevel * 100)}%
            </Typography>
            <IconButton onClick={handleZoomIn} size="small" disabled={zoomLevel >= 5}>
              <ZoomInIcon />
            </IconButton>
            <IconButton onClick={handleResetZoom} size="small">
              <ResetIcon />
            </IconButton>
          </Box>
        </Box>
        
        <Box
          sx={{
            width: '100%',
            height: 500,
            border: `2px solid ${theme.palette.divider}`,
            borderRadius: 2,
            overflow: 'hidden',
            position: 'relative',
            cursor: zoomLevel > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default',
            bgcolor: alpha(theme.palette.common.black, 0.05)
          }}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        >
          <img
            src={imageUrl.startsWith('http') ? imageUrl : `${process.env.REACT_APP_API_URL}${imageUrl}`}
            alt={altText}
            style={{
              transform: `scale(${zoomLevel}) translate(${imagePosition.x / zoomLevel}px, ${imagePosition.y / zoomLevel}px)`,
              transformOrigin: 'center center',
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              transition: isDragging ? 'none' : 'transform 0.1s ease-out',
              userSelect: 'none',
              pointerEvents: zoomLevel > 1 ? 'auto' : 'none'
            }}
            draggable={false}
          />
        </Box>
        
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          Use zoom controls to examine details. Click and drag to pan when zoomed in.
        </Typography>
      </CardContent>
    </Card>
  );
};

export default ImageViewer;
